import { useEffect, useState } from 'react'
import { useChangeFleetJobCadenceMutation, useFleetJobsQuery } from '../../../api/client'
import { toastError, toastSuccess } from '../../Notification'
import { MAX_INTERVAL_SECS, cadenceText, hoursToIntervalSecs, rangeText } from '../cadence'
import type { WorkRow } from '../work-row'
import GenericParamsDetails from './GenericParamsDetails'

type Props = { row: WorkRow }

/**
 * The settings body for the fleet passes (`api_post_fetch`, `api_comment_fetch`):
 * what the pass does and how often it runs, with the cadence editable in place.
 * The same kinds also run per account; a row whose tag isn't a fleet job gets the
 * generic key-and-value view.
 */
function FleetJobDetails({ row }: Props) {
    const { data } = useFleetJobsQuery({ fetchPolicy: 'cache-first' })
    const job = data?.fleetJobs.find((candidate) => candidate.tag === row.tag)
    if (!job) {
        return <GenericParamsDetails row={row} />
    }
    return (
        <div className="space-y-3 text-sm">
            <div>
                <div className="font-medium text-ink-900">{job.name}</div>
                <p className="text-ink-500">{job.description}</p>
            </div>
            {job.cadenceEditable && row.live && row.intervalSecs != null ? (
                <CadenceEditor
                    jobKey={job.key}
                    rowId={row.rowId}
                    intervalSecs={row.intervalSecs}
                    defaultIntervalSecs={job.defaultIntervalSecs ?? undefined}
                    minIntervalSecs={job.minIntervalSecs ?? 0}
                />
            ) : null}
        </div>
    )
}

type CadenceEditorProps = {
    jobKey: string
    rowId: string
    intervalSecs: number
    defaultIntervalSecs?: number
    minIntervalSecs: number
}

const hoursOf = (secs: number) => String(secs / 3600)

function CadenceEditor({
    jobKey,
    rowId,
    intervalSecs,
    defaultIntervalSecs,
    minIntervalSecs
}: CadenceEditorProps) {
    const [hours, setHours] = useState(hoursOf(intervalSecs))
    // The box follows the live cadence as the page polls, until the admin
    // types in it. Otherwise a cadence another admin changed leaves the old
    // number here, and "Change cadence" would offer to put it back.
    const [edited, setEdited] = useState(false)
    useEffect(() => {
        if (!edited) setHours(hoursOf(intervalSecs))
    }, [intervalSecs, edited])

    // The list holds the row's cadence, so it has to refetch to show the change.
    const [changeCadence, { loading }] = useChangeFleetJobCadenceMutation({
        refetchQueries: ['scheduledWork']
    })

    const wanted = hoursToIntervalSecs(hours, minIntervalSecs)
    const changed = wanted !== undefined && wanted !== intervalSecs
    // The cadence a row starts on lives in the code; once changed here, the
    // database is the only record of it, so the default is shown whenever the
    // two differ. A row that boot recreated after a stop starts on the
    // default again, so its note disappears.
    const differsFromDefault = defaultIntervalSecs != null && intervalSecs !== defaultIntervalSecs

    const onChangeCadence = async () => {
        if (wanted === undefined) return
        try {
            const { data } = await changeCadence({
                variables: { key: jobKey, expectedRowId: rowId, intervalSecs: wanted }
            })
            const result = data?.changeFleetJobCadence
            if (result?.changed) {
                toastSuccess(result.message)
            } else if (result) {
                toastError(result.message)
            }
            setEdited(false)
        } catch (err: any) {
            toastError(err.message)
        }
    }

    return (
        <div>
            <div className="text-ink-700">
                Runs {cadenceText(intervalSecs)}
                {differsFromDefault ? (
                    <span className="text-ink-500">
                        {' '}
                        · default {cadenceText(defaultIntervalSecs!)}
                    </span>
                ) : null}
            </div>
            <div className="mt-2 flex items-center gap-2">
                <label htmlFor={`cadence-${jobKey}`} className="sr-only">
                    Cadence in hours
                </label>
                <input
                    id={`cadence-${jobKey}`}
                    type="number"
                    min={minIntervalSecs / 3600}
                    max={MAX_INTERVAL_SECS / 3600}
                    step={0.5}
                    value={hours}
                    onChange={(event) => {
                        setHours(event.target.value)
                        setEdited(true)
                    }}
                    aria-invalid={wanted === undefined}
                    className="w-20 rounded-md border border-ink-200 px-2 py-1 text-sm text-ink-900 shadow-sm focus:border-arwen-500 focus:ring-arwen-500"
                />
                <span className="text-ink-500">hours</span>
                <button
                    type="button"
                    onClick={onChangeCadence}
                    disabled={!changed || loading}
                    className="px-2.5 py-1.5 rounded-md text-sm font-medium border border-ink-300 text-ink-700 bg-white hover:bg-ink-50 disabled:opacity-50"
                >
                    Change cadence
                </button>
            </div>
            {wanted === undefined ? (
                <div className="mt-1 text-xs text-severe-700">{rangeText(minIntervalSecs)}</div>
            ) : null}
        </div>
    )
}

export default FleetJobDetails
